import React, { useState, useEffect } from 'react';
import { graphql, StaticQuery } from 'gatsby';
import PropTypes from 'prop-types';

import {
  Wrapper,
  StyledContainer,
  StyledBlogPost,
  NoResults,
  ViewMore,
} from './styles';

// * The amount of new posts to be added to the page when a user presses show more
const postIncrement = 4;

/*
 * articles: {array} The array of article objects that is passed in by the wrapping static query
 */

const AllBlogPosts = ({ sectionId, articles, activeFilter, lang }) => {
  if (!articles) return null;

  const [currentPosts, setCurrentPosts] = useState([]);
  const [maxCount, setMaxCount] = useState(1);
  const [disabled, setDisabled] = useState(false);
  // * Stores the articles that should be visible in state
  useEffect(() => {
    const newPosts = articles
      .filter(({ category }) => {
        return category.find(el => el.id !== 'prodigy-culture');
      })
      .filter(({ category }) => {
        const matchesFilter = activeFilter
          ? category.find(el => {
              return el.id === activeFilter.value;
            })
          : true;
        return !!matchesFilter;
      });
    setCurrentPosts(newPosts);
    setMaxCount(1);
  }, [activeFilter]);

  // * Checks to see when we are showing all available posts, and then disables the ability to View more
  useEffect(() => {
    setDisabled(postIncrement * maxCount >= currentPosts.length);
  }, [currentPosts, maxCount]);

  return (
    <Wrapper>
      <StyledContainer
        id={sectionId}
        className={activeFilter ? 'reduced-margin' : ''}
      >
        {currentPosts.length === 0 && <NoResults>No Results Found</NoResults>}
        {currentPosts.map((post, index) => {
          if (index >= postIncrement * maxCount) return null;
          return <StyledBlogPost key={post.id} data={post} lang={lang} />;
        })}
        <ViewMore onClick={() => setMaxCount(maxCount + 1)} disabled={disabled}>
          View more posts
        </ViewMore>
      </StyledContainer>
    </Wrapper>
  );
};

AllBlogPosts.propTypes = {
  articles: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  activeFilter: PropTypes.shape(),
  sectionId: PropTypes.string,
  lang: PropTypes.string,
};

AllBlogPosts.defaultProps = {
  activeFilter: null,
  sectionId: null,
  lang: 'en-ca',
};

// * StaticQuery Component that retrieves and passes in all the articles
const withStaticQueryBlogPosts = ({ activeFilter, lang }) => {
  return (
    <StaticQuery
      query={graphql`
        {
          prodigy {
            pagedArticles {
              id
              lang
              url
              author {
                name {
                  text
                }
                description {
                  text
                }
              }
              publishDate
              category {
                text
                id
                title
              }
              title
              titleShort
              metaData {
                metaDescription
                metaNoIndex
              }
              headingData {
                image {
                  url
                }
                imageExpanded {
                  imageBlogArticle {
                    url
                    altTag
                  }
                  imageBlogArticleSharp {
                    publicURL
                    childImageSharp {
                      gatsbyImageData(
                        quality: 90
                        placeholder: BLURRED
                        layout: FULL_WIDTH
                        formats: [AUTO, WEBP]
                      )
                    }
                  }
                  imageThumbnail {
                    url
                    altTag
                  }
                  imageThumbnailSharp {
                    publicURL
                    childImageSharp {
                      gatsbyImageData(
                        quality: 90
                        placeholder: BLURRED
                        layout: FULL_WIDTH
                        formats: [AUTO, WEBP]
                      )
                    }
                  }
                  imageFullsize {
                    url
                    altTag
                  }
                  imageFullsizeSharp {
                    publicURL
                    childImageSharp {
                      gatsbyImageData(
                        quality: 90
                        placeholder: BLURRED
                        layout: FULL_WIDTH
                        formats: [AUTO, WEBP]
                      )
                    }
                  }
                  imageCard {
                    url
                    altTag
                  }
                  imageCardSharp {
                    publicURL
                    childImageSharp {
                      gatsbyImageData(
                        quality: 90
                        placeholder: BLURRED
                        layout: FULL_WIDTH
                        formats: [AUTO, WEBP]
                      )
                    }
                  }
                }
                teaserTextHtml
                teaserTextShortHtml
              }
            }
          }
        }
      `}
      render={({ prodigy }) => {
        const localizedArticles = prodigy.pagedArticles.filter(
          article => article.lang === (lang !== 'en-in' ? 'en-ca' : 'en-in')
        );
        return (
          <AllBlogPosts
            articles={localizedArticles}
            activeFilter={activeFilter}
            lang={lang}
          />
        );
      }}
    />
  );
};

withStaticQueryBlogPosts.propTypes = {
  activeFilter: PropTypes.shape(),
};

withStaticQueryBlogPosts.defaultProps = {
  activeFilter: null,
};

export default withStaticQueryBlogPosts;
